import Vue from 'vue';
import Router from 'vue-router';
import { setUser } from '@sentry/vue';
import store from '@/store';
import { endWebRTCSession } from '@/weon/web-rtc/WeonWebRTC.js';
import { unsubscribeWebSocketStompAndDisconnect } from '@/weon/message-broker/Client';

// modules
import EmailRoute from '@/modules/email/infra/routes/EmailRoute';
import AttendanceRoute from '@/modules/attendance/infrastructure/routes';

const Application = () => import('../components/pages/Application');
const Login = () => import('../components/pages/Login');
const ResetPassword = () => import('../components/pages/ResetPassword');
const Dashboard = () => import('@/components/dashboard/Dashboard');
const UnsuportedBrowser = () => import('@/components/pages/UnsuportedBrowser');
const TabDisabled = () => import('@/components/pages/TabDisabled');
const ContactManagement = () =>
  import('@/components/contact-management/ContactManagement.vue');
const EmployerManagement = () =>
  import('@/components/employer/EmployerManagement.vue');
const ContactInformationView = () =>
  import('@/components/contact/details/ContactInformationView.vue');
const EmployerInformationView = () =>
  import('@/components/employer/EmployerInformationView.vue');
const Colleagues = () => import('@/components/colleagues/Colleagues.vue');
const HistoryPage = () => import('@/components/pages/HistoryPage');
const BroadcastPage = () => import('@/components/pages/BroadcastPage');
const LostConnection = () => import('@/components/pages/LostConnection.vue');

Vue.use(Router);

const router = new Router({
  base: process.env.BASE_URL,
  mode: 'history',
  routes: [
    {
      path: '/unsupported-browser',
      name: 'UnsuportedBrowser',
      component: UnsuportedBrowser,
    },
    {
      path: '/tab-disabled',
      name: 'TabDisabled',
      component: TabDisabled,
    },
    {
      path: '/lost-connection',
      name: 'LostConnection',
      component: LostConnection,
    },
    {
      path: '/',
      name: 'application',
      component: Application,
      redirect: 'attendance/',
      meta: {
        requireAuth: true,
      },
      children: [
        ...EmailRoute,
        ...AttendanceRoute,
        {
          path: 'history',
          name: 'history',
          component: HistoryPage,
        },
        {
          path: 'broadcast',
          name: 'broadcast',
          component: BroadcastPage,
        },
        {
          path: 'dashboard',
          name: 'dashboard',
          component: Dashboard,
        },
        {
          path: 'contact-management',
          name: 'contact-management',
          component: ContactManagement,
        },
        {
          path: 'employer-management',
          name: 'employer-management',
          component: EmployerManagement,
        },
        {
          path: 'colleagues',
          name: 'colleagues',
          component: Colleagues,
        },
      ],
    },
    {
      path: '/contact',
      name: 'contact',
      redirect: { name: 'ContactInformation' },
      component: Application,
      children: [
        {
          path: ':id',
          name: 'ContactInformation',
          component: ContactInformationView,
        },
      ],
      meta: {
        requireAuth: true,
      },
    },
    {
      path: '/employer',
      name: 'employer',
      redirect: { name: 'EmployerInformation' },
      component: Application,
      children: [
        {
          path: ':id',
          name: 'EmployerInformation',
          component: EmployerInformationView,
        },
      ],
      meta: {
        requireAuth: true,
      },
    },
    {
      path: '/login',
      name: 'Login',
      component: Login,
      meta: {
        requireAuth: false,
      },
    },
    {
      path: '/reset-password',
      name: 'ResetPassword',
      component: ResetPassword,
      meta: {
        requireAuth: false,
      },
    },
    {
      path: '*',
      redirect: '/attendance',
    },
  ],
});

router.beforeEach(async (to, from, next) => {
  if (!localStorage.getItem('token') && to.name != 'Login') {
    await endWebRTCSession();
    unsubscribeWebSocketStompAndDisconnect();
    store.dispatch('resetState');
    indexedDB.deleteDatabase('localforage');
    next({ path: '/login' });
    return;
  }

  store.dispatch('setCurrentNavigationMenu', to.name);

  if (to.meta.emailCredentials) {
    handleEmailCredentials();
  }

  if (from.name !== 'Login' && to.name === 'ResetPassword') {
    return next({ path: '/' });
  }

  // Informação do agent para monitoramento
  trackAgentInfo();
  next();
});

// Seta informações do agente para o Usetiful
window.usetifulTags = {
  userId: store.getters['attendance/attendance']?.agentId,
  firstName: store.getters['attendance/attendance']?.agentName,
  lastName: store.getters['attendance/attendance']?.email,
};

let emailCheckTimeout = null;
function handleEmailCredentials(next) {
  if (emailCheckTimeout) {
    clearTimeout(emailCheckTimeout);
  }

  emailCheckTimeout = setTimeout(() => {
    const credentials = store.state.email.emailCredentials;
    if (!credentials || credentials.status === 'FAILED') {
      next({ name: 'email.settings' });
    } else {
      next();
    }
  }, 1000);
}

let countNavigation = 0;
function trackAgentInfo() {
  const attendance = store.getters['attendance/attendance'];
  if (!attendance.agentId || countNavigation >= 1) return;
  countNavigation += 1;

  const { agentName, email, loginIdentifier } = attendance;
  const client = window.location.hostname.split('.')[0];
  setUser({ agentName, email, loginIdentifier, client });
}

export default router;
